import { AthenaConfigurationError } from "../config/errors.ts";
import { recordPostgresPoolCreated } from "../runtime/ownership.ts";
import {
	ATHENA_POSTGRES_POOL_DEFAULTS,
	type AthenaPostgresClient,
	type AthenaPostgresPool,
	type AthenaPostgresPoolDiagnostics,
	createPostgresPool,
	postgresPoolDiagnostics,
	type QueryResult,
	type QueryResultRow,
} from "./driver.ts";
import { assertNodePostgresRuntime } from "./runtime.ts";

function isPostgresDebugEnabled(): boolean {
	const value = (
		globalThis as { process?: { env?: Record<string, string | undefined> } }
	).process?.env?.ATHENA_JS_DEBUG;
	return value === "1" || value === "true";
}

function logPoolSaturation(
	stats: AthenaPostgresPoolDiagnostics,
	durationMs: number,
): void {
	if (!isPostgresDebugEnabled()) {
		return;
	}
	if (stats.waitingCount <= 0 && !(stats.idleCount === 0 && durationMs >= 50)) {
		return;
	}
	console.warn(
		`[athena-postgres] pool total=${stats.totalCount} idle=${stats.idleCount} waiting=${stats.waitingCount} queryMs=${Math.round(durationMs)}`,
	);
}

export type AthenaResourceOwnership = "owned" | "borrowed";

export interface AthenaOwnedResource<T> {
	readonly ownership: AthenaResourceOwnership;
	readonly resource: T;
}

export type AthenaPostgresTransactionOptions = {
	deadlineMs?: number;
};

export interface AthenaPostgresRuntime {
	close(): Promise<void>;
	getPool(): Promise<AthenaPostgresPool>;
	inspectPool(): Promise<AthenaPostgresPoolDiagnostics>;
	readonly ownership: AthenaResourceOwnership;
	query<T extends QueryResultRow = QueryResultRow>(
		text: string,
		values?: unknown[],
	): Promise<QueryResult<T>>;
	transaction<T>(
		fn: (runtime: AthenaPostgresRuntime) => Promise<T>,
		options?: AthenaPostgresTransactionOptions,
	): Promise<T>;
}

export function connectPostgresPoolWithDeadline(
	pool: AthenaPostgresPool,
	deadlineMs: number,
): Promise<AthenaPostgresClient> {
	let timer: ReturnType<typeof setTimeout> | undefined;
	let timedOut = false;
	const connecting = pool.connect().then((client) => {
		if (timedOut) {
			client.release();
			throw postgresConnectDeadlineError();
		}
		return client;
	});
	connecting.catch(() => undefined);
	return Promise.race([
		connecting,
		new Promise<never>((_, reject) => {
			timer = setTimeout(() => {
				timedOut = true;
				reject(postgresConnectDeadlineError());
			}, deadlineMs);
		}),
	]).finally(() => {
		if (timer !== undefined) {
			clearTimeout(timer);
		}
	});
}

function postgresConnectDeadlineError(): Error {
	return new Error("connection timeout exceeded");
}

export interface CreateAthenaPostgresRuntimeOptions {
	connectionString?: string;
	ownership?: AthenaResourceOwnership;
	pool?: AthenaPostgresPool;
}

const runtimeByTransport = new WeakMap<object, AthenaPostgresRuntime>();

const OWNED_RUNTIME_CACHE = Symbol.for(
	"@xylex-group/athena.ownedPostgresRuntimes",
);

type OwnedRuntimeCacheEntry = {
	refs: number;
	runtime: AthenaPostgresRuntime;
};

type OwnedRuntimeCache = Map<string, OwnedRuntimeCacheEntry>;

function ownedRuntimeCache(): OwnedRuntimeCache {
	const holder = globalThis as typeof globalThis & {
		[OWNED_RUNTIME_CACHE]?: OwnedRuntimeCache;
	};
	holder[OWNED_RUNTIME_CACHE] ??= new Map();
	return holder[OWNED_RUNTIME_CACHE];
}

export function bindPostgresRuntime(
	transport: object,
	runtime: AthenaPostgresRuntime,
): void {
	runtimeByTransport.set(transport, runtime);
}

export function getBoundPostgresRuntime(
	transport: object | undefined,
): AthenaPostgresRuntime | undefined {
	return transport ? runtimeByTransport.get(transport) : undefined;
}

export function createAthenaPostgresRuntime(
	options: CreateAthenaPostgresRuntimeOptions,
): AthenaPostgresRuntime {
	const connectionString = options.connectionString?.trim();
	if (!(options.pool || connectionString)) {
		throw new AthenaConfigurationError(
			"ATHENA_RUNTIME_CONFIG_INVALID",
			"AthenaPostgresRuntime requires a connection string or an existing pool.",
			"db",
		);
	}

	assertNodePostgresRuntime();

	const ownership: AthenaResourceOwnership = options.pool
		? (options.ownership ?? "borrowed")
		: "owned";

	if (ownership === "owned" && connectionString && !options.pool) {
		const cached = ownedRuntimeCache().get(connectionString);
		if (cached) {
			cached.refs += 1;
			return cached.runtime;
		}
	}

	let poolPromise: Promise<AthenaPostgresPool> | undefined;
	let closed = false;

	const getPool = async (): Promise<AthenaPostgresPool> => {
		if (closed) {
			throw new AthenaConfigurationError(
				"ATHENA_RUNTIME_DISPOSED",
				"Athena PostgreSQL runtime was disposed.",
				"db",
			);
		}
		if (options.pool) {
			return options.pool;
		}
		poolPromise ??= createPostgresPool(connectionString as string);
		return poolPromise;
	};

	const inspectPool = async (): Promise<AthenaPostgresPoolDiagnostics> =>
		postgresPoolDiagnostics(await getPool());

	const runtime: AthenaPostgresRuntime = {
		async close() {
			if (connectionString && ownership === "owned") {
				const entry = ownedRuntimeCache().get(connectionString);
				if (entry && entry.runtime === runtime) {
					entry.refs -= 1;
					if (entry.refs > 0) {
						return;
					}
					ownedRuntimeCache().delete(connectionString);
				}
			}
			if (closed) {
				return;
			}
			closed = true;
			if (ownership !== "owned" || !poolPromise) {
				return;
			}
			const pool = await poolPromise.catch(() => undefined);
			poolPromise = undefined;
			if (pool) {
				await pool.end();
			}
		},
		getPool,
		inspectPool,
		ownership,
		async query(text, values) {
			const pool = await getPool();
			const stats = postgresPoolDiagnostics(pool);
			const started = performance.now();
			try {
				return await pool.query(text, values);
			} finally {
				logPoolSaturation(stats, performance.now() - started);
			}
		},
		async transaction(fn, options) {
			const pool = await getPool();
			const deadlineMs =
				options?.deadlineMs ??
				ATHENA_POSTGRES_POOL_DEFAULTS.connectionTimeoutMillis;
			const client = await connectPostgresPoolWithDeadline(pool, deadlineMs);
			const txRuntime: AthenaPostgresRuntime = {
				close: async () => { },
				getPool,
				inspectPool,
				ownership: "borrowed",
				query: async (text, values) => client.query(text, values),
				transaction: (inner) => inner(txRuntime),
			};
			try {
				await client.query("BEGIN");
				const value = await fn(txRuntime);
				await client.query("COMMIT");
				return value;
			} catch (error) {
				try {
					await client.query("ROLLBACK");
				} catch {
					// ignore rollback failure
				}
				throw error;
			} finally {
				client.release();
			}
		},
	};

	if (ownership === "owned" && connectionString && !options.pool) {
		ownedRuntimeCache().set(connectionString, { refs: 1, runtime });
		recordPostgresPoolCreated();
	}

	return runtime;
}

export function getAthenaOwnedRuntimeCacheSize(): number {
	return ownedRuntimeCache().size;
}
